/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef } from 'react';
import { Drawer, IconButton, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { FieldLabel, CoverImageUpload, TierButton } from '../shared/SharedComponents';
import adminServices from '../../../../../services/adminServices';


const MAX_BULLETS = 7;
const BULLET_MAX_CHARS = 80;
const SelectWithIcon = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer pr-9"
    >
      {options.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
  </div>
);

const ASSESSMENT_TYPES = [
  { key: 'FULL_TEST', label: 'Full Tests', desc: 'Complete mock exams' },
  { key: 'SUBJECT_TEST', label: 'Subject Tests', desc: 'Single-subject assessments' },
  { key: 'CHAPTER_TEST', label: 'Chapter Tests', desc: 'Topic-level short tests' },
];
const billingTypes = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'one_time', label: 'One Time' },
];

const BILLING_CYCLES = ['Monthly', 'Yearly'];

const SectionHeader = ({ num, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <span className="flex-shrink-0 w-6 h-6 rounded-full border border-gray-300 text-xs font-semibold text-gray-500 flex items-center justify-center mt-0.5">
      {num}
    </span>
    <div>
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const SectionDivider = () => <div className="border-t border-gray-100 my-6" />;


/** Section 1 – Plan Identity */
const PlanIdentitySection = ({ values, set, onTierChange, nameError, clearNameError }) => (
  <div>
    <SectionHeader num={1} title="Plan Identity" subtitle="Internal name and customer-facing display fields." />

    <div className="mb-4">
      <FieldLabel required>Plan name (internal)</FieldLabel>
      <input
        type="text"
        value={values.name || ''}
        // onChange={(e) => set('name', e.target.value)}
        onChange={(e) => { set('name', e.target.value); clearNameError?.(); }}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
      />
      {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
    </div>


    <div className="mb-4">
      <FieldLabel>Display name (customer-facing)</FieldLabel>
      <input
        type="text"
        value={values.displayName || ''}
        placeholder="e.g. Pro Plan"
        onChange={(e) => set('displayName', e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
      />
      <p className="text-xs text-gray-400 mt-1">Shown on pricing page. Defaults to plan name if blank.</p>
    </div>

    <div className="mb-4">
      <FieldLabel>Tagline</FieldLabel>
      <input
        type="text"
        value={values.tagline || ''}
        placeholder="One-sentence pricing card subtitle"
        onChange={(e) => set('tagline', e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
      />
    </div>

    <div className="mb-4">
      <FieldLabel>Description</FieldLabel>
      <textarea
        rows={3}
        value={values.description || ''}
        placeholder="e.g. Full access to all exams with detailed analytics"
        onChange={(e) => set('description', e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 resize-none placeholder-gray-400"
      />
    </div>
    <div className="mb-4">
      <FieldLabel required>Billing Type</FieldLabel>
      <div className="flex gap-[10px] opacity-60 cursor-not-allowed">
        {billingTypes.map(({ value, label }) => (
          <TierButton
            key={value}
            label={label}
            selected={values.billingtype === value}
            onClick={() => { }}
          />
        ))}
      </div>
    </div>
    <div className="mb-4">
      <FieldLabel required>Tier</FieldLabel>
      <div className="flex gap-2">
        {['BASIC', 'PRO', 'PREMIUM', 'FREE'].map((t) => (
          <button disabled
            key={t}
            type="button"
            onClick={() => onTierChange(t)}
            className={`px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all duration-150
              ${values.tier === t
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
              }`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>

    {/* Most Popular badge toggle */}
    <div className="flex items-center justify-between py-2 mb-4">
      <div>
        <p className="text-sm text-gray-900">Most Popular badge</p>
        <p className="text-xs text-gray-400">Shows &quot;Most Popular&quot; badge on pricing card</p>
      </div>
      <Switch
        checked={!!values.mostPopular}
        onChange={(e) => set('mostPopular', e.target.checked)}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2563eb' },
        }}
      />
    </div>

    <div className="mb-4">
      <FieldLabel>CTA button label</FieldLabel>
      <input
        type="text"
        value={values.ctaLabel || ''}
        placeholder='Default: "Get Started"'
        onChange={(e) => set('ctaLabel', e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
      />
    </div>


  </div>
);

const ScopeSection = ({ values, set, examCategories }) => (
  <div>
    <SectionHeader num={2} title="Scope" subtitle="Platform-wide or single exam category." />

    <div className="grid grid-cols-2 gap-2 mb-4">
      {[
        { key: 'platform', label: 'Platform-wide', sub: 'All exam categories' },
        { key: 'category', label: 'Category Bundle', sub: 'One exam category only' },
      ].map((s) => (
        <button disabled
          key={s.key}
          type="button"
          onClick={() => set('scope', s.key)}
          className={`text-left px-4 py-3 rounded-lg border-2 transition-all duration-150
            ${values.scope === s.key
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
        >
          <p className={`text-sm font-semibold ${values.scope === s.key ? 'text-blue-700' : 'text-gray-800'}`}>
            {s.label}
          </p>
          <p className={`text-xs mt-0.5 ${values.scope === s.key ? 'text-blue-500' : 'text-gray-400'}`}>
            {s.sub}
          </p>
        </button>
      ))}
    </div>

    {values.scope === 'category' && (
      <div>
        <FieldLabel required>Exam category</FieldLabel>
        <div className="flex flex-wrap gap-2 mt-1">
          {(examCategories || []).map((cat) => (
            <button disabled
              key={cat.code}
              type="button"
              onClick={() => set('category', cat.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150
                ${values.category === cat.id
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              {cat.code}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

/** Section 3 – Pricing */
const PricingSection = ({ values, set, inrError, usdError, setInrError, setUsdError }) => (
  <div>
    <SectionHeader num={3} title="Pricing" />

    <div className="grid grid-cols-2 gap-3">
      <div>
        <FieldLabel required>
          Price (₹/{values.billingCycle === 'Yearly' ? 'year' : 'month'})
        </FieldLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">₹</span>
          <input
            type="number"
            value={values.price ?? ''}
            disabled
            className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm text-gray-500 bg-gray-50 outline-none cursor-not-allowed"
          />
        </div>
        {inrError && <p className="text-xs text-red-500 mt-1">{inrError}</p>}
      </div>

      <div>
        <FieldLabel required>
          Price (USD/{values.billingCycle === 'Yearly' ? 'year' : 'month'})
        </FieldLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
          <input
            type="number"
            value={values.priceUSD ?? ''}
            disabled
            className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm text-gray-500 bg-gray-50 outline-none cursor-not-allowed"
          />
        </div>
        {usdError && <p className="text-xs text-red-500 mt-1">{usdError}</p>}
      </div>

      {/* Billing cycle — spans full width */}
      <div className="col-span-2">
        <FieldLabel>Billing cycle</FieldLabel>
        <div className="flex border border-gray-200 rounded-[7px] overflow-hidden mt-1 opacity-60 cursor-not-allowed">
          {['Monthly', 'Yearly'].map((opt) => (
            <button
              key={opt}
              type="button"
              disabled
              className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium select-none cursor-not-allowed"
              style={{
                background: values.billingCycle === opt ? '#eff6ff' : '#fff',
                color: values.billingCycle === opt ? '#1d4ed8' : '#374151',
                border: values.billingCycle === opt ? '1.5px solid #2563eb' : '1px solid transparent',
                borderRadius: values.billingCycle === opt ? '6px' : 0,
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);
/** Section 4 – Access Rules */
const AccessRulesSection = ({ values, set, error }) => {
  const toggleType = (type) => {
    const current = values.assessmentTypes || [];
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    set('assessmentTypes', updated);
  };

  return (
    <div>
      <SectionHeader num={4} title="Access Rules" />

      <div className="mb-4">
        <FieldLabel required>Allowed assessment types</FieldLabel>
        {error && (
          <p className="text-xs text-red-500 mb-1">{error}</p>
        )}
        <div className="grid grid-cols-3 gap-2 mt-1">
          {ASSESSMENT_TYPES.map((t) => {
            const active = (values.assessmentTypes || []).includes(t.key);
            const isLocked =
              (values.tier === 'BASIC' && t.key !== 'FULL_TEST') ||
              (values.tier === 'PRO' && t.key === 'CHAPTER_TEST'); return (
                <button
                  key={t.key} type="button" onClick={() => !isLocked && toggleType(t.key)} disabled={isLocked}
                  className={`text-left p-3 rounded-lg border-2 transition-all duration-150
                    ${isLocked
                      ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-50'
                      : active
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                >
                  <p className={`text-xs font-semibold ${active && !isLocked ? 'text-blue-700' : 'text-gray-400'}`}>
                    {t.label}
                  </p>
                  <p className={`text-xs mt-0.5 leading-tight ${active && !isLocked ? 'text-blue-500' : 'text-gray-400'}`}>
                    {isLocked ? 'Not available on this tier' : t.desc}
                  </p>
                </button>
              );
          })}
        </div>
      </div>

      <div>
        <FieldLabel>Max paid attempts per assessment</FieldLabel>
        <div className="flex items-center gap-3 mt-1">
          <input
            type="number"
            value={values.maxAttempts || 5}
            onChange={(e) => set('maxAttempts', e.target.value)}
            className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-xs">ℹ</span>
            Free attempt always included
          </p>
        </div>
      </div>
    </div>
  );
};

/** Section 5 – Feature Bullets */
const FeatureBulletsSection = ({ values, set }) => {
  const bullets = values.featureBullets || ['', '', ''];

  const updateBullet = (i, val) => {
    const updated = [...bullets];
    updated[i] = val.slice(0, BULLET_MAX_CHARS);
    set('featureBullets', updated);
  };

  const addBullet = () => {
    if (bullets.length < MAX_BULLETS) {
      set('featureBullets', [...bullets, '']);
    }
  };

  const removeBullet = (i) => {
    const updated = bullets.filter((_, idx) => idx !== i);
    set('featureBullets', updated);
  };

  return (
    <div>
      <SectionHeader
        num={5}
        title="Feature Bullets"
        subtitle={`${bullets.length} of ${MAX_BULLETS} max · ${BULLET_MAX_CHARS} chars each`}
      />

      <div className="space-y-2 mb-3">
        {bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-gray-300 cursor-grab select-none text-base">⠿</span>
            <div className="flex-1 relative">
              <input
                type="text"
                value={b}
                placeholder={`Feature ${i + 1}`}
                onChange={(e) => updateBullet(i, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400 pr-16"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-300 pointer-events-none whitespace-nowrap">
                {b.length}/{BULLET_MAX_CHARS}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeBullet(i)}
              className="text-gray-300 hover:text-red-400 transition-colors text-lg leading-none flex-shrink-0"
              title="Remove"
            >
              🗑
            </button>
          </div>
        ))}
      </div>

      {bullets.length < MAX_BULLETS && (
        <button
          type="button"
          onClick={addBullet}
          className="text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center gap-1 mb-4"
        >
          + Add Bullet
        </button>
      )}

      <div className="mt-4">
        <FieldLabel>Footnote</FieldLabel>
        <input
          type="text"
          value={values.footnote || ''}
          placeholder="Small print below CTA on pricing card"
          onChange={(e) => set('footnote', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
        />
      </div>
    </div>
  );
};

const AddAssessmentsSection = ({ assessmentFetchFn, values, set, tier, scope, selectedCategory, examCategories, subscriptionTypeCode, existingAssessmentIds = [] }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingItems, setLoadingItems] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const hasFetched = useRef(false);


  useEffect(() => {
    if (!hasFetched.current && typeof assessmentFetchFn === 'function') {
      hasFetched.current = true;
      setLoadingItems(true);

      assessmentFetchFn({ page: 1, pageSize: 100 })
        .then((res) => {
          if (res?.ok) {
            const list = Array.isArray(res.data?.rows)
              ? res.data.rows
              : Array.isArray(res.data)
                ? res.data
                : [];

            setItems(list);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingItems(false));
    }
  }, [assessmentFetchFn]);
 
  const selected = (values.selectedAssessmentIds || []).map(Number);

  const toggle = (id) => {
    const numId = Number(id);

    const updated = selected.includes(numId)
      ? selected.filter((x) => x !== numId)
      : [...selected, numId];

    set('selectedAssessmentIds', updated);
  };

  const safeItems = Array.isArray(items) ? items : [];

  const filtered = safeItems.filter((a) => {
    const searchLower = search.toLowerCase();

    const matchesSearch =
      (a.title || '').toLowerCase().includes(searchLower) ||
      (a.type || '').toLowerCase().includes(searchLower) ||
      (a.assessment_sub_type_description || '')
        .toLowerCase()
        .includes(searchLower);

    const examType = (
      a.type ||
      a.assessment_type_code ||
      ''
    ).toUpperCase();

    const selectedCategoryCode =
      (examCategories || [])
        .find((c) => Number(c.id) === Number(selectedCategory))
        ?.code?.toUpperCase() || '';

    const categoryOk =
      scope !== 'category' ||
      !selectedCategory ||
      examType === selectedCategoryCode;

    const subTypeDesc = (
      a.assessment_sub_type_description || ''
    ).toUpperCase();

    const isFullTest = subTypeDesc === 'FULL TEST';
    const isSubject = subTypeDesc === 'SUBJECT TEST';
    const isChapter = subTypeDesc.includes('CHAPTER TEST');

    const tierOk = !tier
      ? true
      : tier === 'BASIC'
        ? isFullTest
        : tier === 'PRO'
          ? isFullTest || isSubject
          : tier === 'PREMIUM'
            ? isFullTest || isSubject || isChapter
            : true;

    const audienceOk =
      subscriptionTypeCode === 'B2B'
        ? a.audience === 'B2B' || a.audience === 'BOTH'
        : a.audience === 'B2C' || a.audience === 'BOTH';

    return (
      matchesSearch &&
      tierOk &&
      categoryOk &&
      audienceOk
    );
  }).sort((a, b) => {
    const aExisting = existingAssessmentIds.includes(Number(a.id || a._id));
    const bExisting = existingAssessmentIds.includes(Number(b.id || b._id));
    if (aExisting === bExisting) return 0;
    return aExisting ? -1 : 1;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const paginated = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
 
  return (
    <div>
      <SectionHeader
        num={6}
        title="Add Assessments"
        subtitle="Optionally add more LIVE B2C assessments. Existing content managed via plan detail page."
      />
 
      <div className="relative mb-2">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
          🔍
        </span>

        <input
          type="text"
          value={search}
          placeholder="Search assessments..."
          onChange={handleSearch}
          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
        />
      </div>
 
      <div className="border border-gray-200 rounded-lg overflow-y-auto max-h-44">
        {loadingItems ? (
          <div className="py-6 text-center text-xs text-gray-400">
            Loading assessments…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-xs text-gray-400">
            No assessments found
          </div>
        ) : (
          paginated.map((a) => {
            const id = a.id || a._id;

            const isChecked = selected.includes(Number(id));

            const isExisting = existingAssessmentIds.includes(
              Number(id)
            );

            const examCategory =
              a.type || a.assessment_type_code || '';

            const subType =
              a.assessment_sub_type_description || '';

            const typeLabel =
              examCategory && subType
                ? `${examCategory} · ${subType}`
                : examCategory || subType || '';

            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 ${isExisting
                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                  : 'cursor-pointer hover:bg-gray-50'
                  }`}
                onClick={() => !isExisting && toggle(id)}
                onKeyDown={(e) =>
                  !isExisting &&
                  (e.key === 'Enter' || e.key === ' ') &&
                  toggle(id)
                }
              >
                <input
                  type="checkbox"
                  checked={isExisting || isChecked}
                  disabled={isExisting}
                  onChange={() => !isExisting && toggle(id)}
                  className="mt-0.5 accent-blue-600 flex-shrink-0"
                />

                <div>
                  <p className="text-sm text-gray-900">
                    {a.title || a.name}
                  </p>

                  {typeLabel && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {typeLabel}
                    </p>
                  )}

                  {isExisting && (
                    <p className="text-xs text-blue-500 mt-0.5">
                      Already in plan
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
 
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            type="button"
            onClick={() =>
              setPage((p) => Math.max(1, p - 1))
            }
            disabled={page === 1}
            className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>

          <span className="text-xs text-gray-400">
            Page {page} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() =>
              setPage((p) =>
                Math.min(totalPages, p + 1)
              )
            }
            disabled={page === totalPages}
            className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}

      {selected.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          {selected.length} selected
        </p>
      )}
    </div>
  );
};
 
const B2BEditSection = ({ values, set }) => {
  const Num = ({ n }) => (
    <div className="flex-shrink-0 w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center">
      <span className="text-xs font-semibold text-gray-500">{n}</span>
    </div>
  );

  const SectionTitle = ({ n, title }) => (
    <div className="flex items-center gap-3 mb-3">
      <Num n={n} />
      <p className="text-sm font-semibold text-gray-900">{title}</p>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">

      {/* 1. Plan Identity */}
      <div>
        <SectionTitle n={1} title="Plan Identity" />
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel required>Plan name (internal)</FieldLabel>
            <input
              type="text"
              value={values.name || ''}
              onChange={(e) => set('name', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
          </div>
          <div>
            <FieldLabel>Description</FieldLabel>
            <textarea
              rows={3}
              value={values.description || ''}
              onChange={(e) => set('description', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 resize-none"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* 2. Scope */}
      <div>
        <SectionTitle n={2} title="Scope" />
        <div className="border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <span className="text-xs font-semibold text-blue-600 border border-blue-300 bg-blue-50 rounded-full px-3 py-[2px] whitespace-nowrap">
            Platform-Wide
          </span>
          <p className="text-sm text-gray-400">B2B plans are always platform-wide.</p>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* 3. Pricing */}
      <div>
        <SectionTitle n={3} title="Pricing" />
        <div className="border border-gray-200 rounded-lg px-4 py-3">
          <p className="text-sm text-gray-600">Pricing is managed per tenant via the Contract tab.</p>
          <p className="text-xs text-gray-400 mt-1">No price is set on B2B plans.</p>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* 4. Access Rules */}
      <div>
        <SectionTitle n={4} title="Access Rules" />
        <div className="flex flex-col gap-4">
          <div className="border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-sm font-medium text-gray-700">Assessment types</p>
            <p className="text-xs text-gray-400 mt-1">B2B plans grant access to all assessment types.</p>
          </div>

        </div>
      </div>

    </div>
  );
};

const renderField = (f, values, set, fieldErrors, clearError) => {
  const errors = fieldErrors || {};
  const onClear = clearError || (() => { });
  return (
    <div key={f.key} className="mb-4">
      <FieldLabel required={f.required}>{f.label}</FieldLabel>
      {f.multiline ? (
        <textarea
          rows={3}
          value={values[f.key] ?? ''}
          placeholder={f.placeholder || ''}
          onChange={(e) => set(f.key, e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400 resize-none"
        />
      ) : (
        <input
          type={f.type || 'text'}
          value={values[f.key] ?? ''}
          placeholder={f.placeholder || ''}
          onChange={(e) => { set(f.key, e.target.value); onClear(f.key); }}
          disabled={f.disabled}
          className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 placeholder-gray-400
          ${f.disabled
              ? 'bg-gray-50 cursor-not-allowed border-gray-300'
              : errors[f.key]
                ? 'bg-white border-red-400 focus:border-red-500 focus:ring-red-200'
                : 'bg-white border-gray-300 focus:border-blue-600 focus:ring-blue-600'
            }`}
        />
      )}
      {errors[f.key]
        ? <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>
        : f.helper && <p className="text-xs text-gray-400 mt-1">{f.helper}</p>
      }
    </div>
  );
};


const FIELDS_BY_TYPE = {
  single: [
    { key: 'name', label: 'Plan name (internal)', required: true, section: 'PLAN IDENTITY' },
    { key: 'displayName', label: 'Display name (customer-facing)', section: 'PLAN IDENTITY' },
    { key: 'description', label: 'Description', multiline: true, section: 'PLAN IDENTITY' },
    { key: 'price', label: 'Price (₹)', type: 'number', disabled: true, section: 'PRICING' },
    { key: 'priceUSD', label: 'Price (USD)', type: 'number', disabled: true, section: 'PRICING' },

    {
      key: 'stripePriceId', label: 'Stripe Price ID', disabled: true,
      helper: 'Auto-generated on save.', section: 'PRICING'
    },
  ],
  bundle: [
    { key: 'name', label: 'Plan name (internal)', required: true },
    { key: 'displayName', label: 'Display name' },
    { key: 'description', label: 'Description', multiline: true },
    { key: 'tagline', label: 'Tagline' },
    { key: 'price', label: 'Price (₹/year)', type: 'number', disabled: true },
    { key: 'priceUSD', label: 'Price (USD/year)', type: 'number', disabled: true },
    { key: 'stripePriceId', label: 'Stripe Price ID', disabled: true, helper: 'Auto-generated on save.' },
  ],
  b2b: [
    { key: 'name', label: 'Plan name', required: true },
    { key: 'description', label: 'Description', multiline: true },
  ],
  ebooks: [
    { key: 'name', label: 'Plan name (internal)', required: true },
    { key: 'displayName', label: 'Display name' },
    { key: 'description', label: 'Description', multiline: true },
    { key: 'tagline', label: 'Tagline' },
    { key: 'price', label: 'Price (₹/year)', type: 'number', disabled: true },
    { key: 'priceUSD', label: 'Price (USD/year)', type: 'number', disabled: true },
    { key: 'stripePriceId', label: 'Stripe Price ID', disabled: true, helper: 'Auto-generated on save.' },
  ],
};

const imageWidthAndHeight = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const EditDrawer = ({ open, plan, planType, onClose, onSave, assessmentFetchFn }) => {
  const [values, setValues] = useState({});
  const [examCategories, setExamCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [snackError, setSnackError] = useState('');
  const [inrError, setInrError] = useState('');
  const [usdError, setUsdError] = useState('');
  const [nameError, setNameError] = useState('')
  const [coverImage, setCoverImage] = useState(null);
  const [coverImgPreview, setCoverImgPreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [coverImageError, setCoverImageError] = useState('');
  const [showDiscard, setShowDiscard] = useState(false);
  const initialSnapshotRef = useRef(null);

  const isDirty = () => {
  if (!initialSnapshotRef.current) return false;
  const currentSnapshot = JSON.stringify({ values, status, existingImageUrl });
  return currentSnapshot !== initialSnapshotRef.current || !!coverImage;
};

const handleCancelClick = () => {
  if (isDirty()) {
    setShowDiscard(true);
  } else {
    onClose();
  }
};

const handleDiscardConfirm = () => {
  setShowDiscard(false);
  onClose();
};

useEffect(() => {
    if (plan) {
      const rawBullets = (() => {
        const b = plan.featureBullets?.length
          ? plan.featureBullets
          : plan.feature_bullets?.length
            ? plan.feature_bullets
            : [];
        if (typeof b === 'string') {
          try {
            const parsed = JSON.parse(b);
            return Array.isArray(parsed) && parsed.length ? parsed : ['', '', ''];
          } catch { return ['', '', '']; }
        }
        return b.length ? b : ['', '', ''];
      })();

      const newValues = {
        name: plan.name || '',
        tagline: plan.tagline || '',
        displayName: (() => {
          try {
            const raw = plan.displayName || plan.shortDescription || plan.short_desc || '';
            if (!raw) return '';
            if (Array.isArray(raw)) return raw[0] || '';
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed[0] || '' : raw;
          } catch {
            return plan.displayName || plan.short_desc || '';
          }
        })(),
        description: plan.description || '',
        price: (plan.prices || []).find((p) => p?.currencyType?.code === 'INR')?.actualCost ?? plan.payableCost ?? '',
        priceUSD: (plan.prices || []).find((p) => p?.currencyType?.code === 'USD')?.actualCost || '',
        stripePriceId: plan.stripePriceId || plan.prices?.[0]?.stripePriceId || '',
        maxAttempts: plan.maxAttempts || 5,
        footnote: plan.footnote || '',
        tier: plan.tier || 'BASIC',
        scope: plan.scope || 'platform',
        category: plan.categoryId || '',
        assessmentTypes: (() => {
          const raw = plan.assessmentTypes || plan.assessment_types;
          if (Array.isArray(raw) && raw.length)
            return raw.map(s => String(s).trim().toUpperCase());
          if (typeof raw === 'string' && raw.length) {
            const cleaned = raw
              .replace(/[{}"]/g, '')
              .split(',')
              .map(s => s.trim().toUpperCase())
              .filter(Boolean);
            if (cleaned.length) return cleaned;
          }
          const t = plan.tier || 'BASIC';
          if (t === 'BASIC') return ['FULL_TEST'];
          if (t === 'PRO') return ['FULL_TEST', 'SUBJECT_TEST'];
          return ['FULL_TEST', 'SUBJECT_TEST', 'CHAPTER_TEST'];
        })(),
        featureBullets: rawBullets,
        mostPopular: plan.mostPopular || false,
        ctaLabel: plan.ctaLabel || '',
        billingCycle: plan.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly',
        selectedAssessmentIds: (plan.selectedAssessmentIds?.length ? plan.selectedAssessmentIds : (plan.assessmentDetils || []).map((a) => a.id)).map(Number),
        billingtype: plan.billing_type,
      };

      const newStatus = plan.isOnline === true || plan.isOnline === 'true' ? 'Published' : 'Draft';
      const newExistingImageUrl = plan.imageUrl || plan.image_url || '';

      setValues(newValues);
      setStatus(newStatus);
      setExistingImageUrl(newExistingImageUrl);
      setCoverImage(null);
      setCoverImgPreview(null);
      setCoverImageError('');

      // snapshot for dirty-checking
      initialSnapshotRef.current = JSON.stringify({
        values: newValues,
        status: newStatus,
        existingImageUrl: newExistingImageUrl,
      });
    }
    if (open) {
      adminServices.getLookUpData('assessment_exam_categories')
        .then(res => { if (res.ok) setExamCategories(res.data?.assessment_exam_categories || []); })
        .catch(() => { });
    }
  }, [plan?.id, open]);

  const set = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const handleCoverFileChange = (e) => {
    const file = e.currentTarget.files?.[0];
    setCoverImage(null);
    setCoverImgPreview(null);
    setCoverImageError('');
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!allowed.includes(file.type)) { setCoverImageError('Only JPG, PNG, BMP allowed'); return; }
    if (file.size > 104857600) { setCoverImageError('Max file size is 100 MB'); return; }
    imageWidthAndHeight(file).then(({ width, height }) => {
      if (width >= 1200 && height >= 400) {
        setCoverImage(file);
        setCoverImgPreview(URL.createObjectURL(file));
      } else {
        setCoverImageError('Image must be at least 1200 × 400 px');
      }
    });
  };

  const handleCoverRemove = () => {
    setCoverImage(null);
    setCoverImgPreview(null);
    setExistingImageUrl('');
    setCoverImageError('');
  };

  const handleTierChange = (newTier) => {
    set('tier', newTier);
    if (newTier === 'BASIC') {
      set('assessmentTypes', ['FULL_TEST']);
    } else if (newTier === 'PRO') {
      set('assessmentTypes', ['FULL_TEST', 'SUBJECT_TEST']);
    } else if (newTier === 'PREMIUM') {
      set('assessmentTypes', ['FULL_TEST', 'SUBJECT_TEST', 'CHAPTER_TEST']);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const isOnlineValue = status === 'Published' ? 'true' : 'false';
    const statusValue = status.toUpperCase();
    if (!values.name || !values.name.trim()) {
      setNameError('Plan name is required.');
      setSaving(false);
      return;
    }
    if (isAssessment && (!values.assessmentTypes || values.assessmentTypes.length === 0)) {
      setSnackError('Please select at least one assessment type.');
      setSaving(false);
      return;
    }
    if (!isB2B && !isFree && (values.price === '' || values.price == null)) {
      setInrError('INR price is required. Enter 0 for free.');
      setSaving(false);
      return;
    }
    if (!isB2B && !isFree && (values.priceUSD === '' || values.priceUSD == null)) {
      setUsdError('USD price is required. Enter 0 for free.');
      setSaving(false);
      return;
    }
    setSnackError('');
    setInrError('');
    setUsdError('');
    setNameError('')
    let payload;
    if (planType === 'b2b') {
      payload = {
        name: values.name,
        description: values.description || '',
        maxAttempts: values.maxAttempts,
        isOnline: isOnlineValue,
        status: statusValue,
        subscriptionType: 'B2B',
        courseIds: JSON.stringify([...new Set((plan?.courses || plan?.courseDetails || []).map(c => Number(c.id)))]),
        assessmentIds: JSON.stringify([
          ...new Set([
            ...(plan?.assessments || plan?.assessmentDetils || []).map(a => Number(a.id)),
            ...(values.selectedAssessmentIds || []).map(Number),
          ])
        ])
      };
    } else {
      payload = {
        name: values.name,
        tagline: values.tagline || '',
        description: values.description || '',
        shortDescription: JSON.stringify(
          (() => {
            const dn = Array.isArray(values.displayName)
              ? values.displayName[0]
              : values.displayName;
            return dn && String(dn).trim() !== '' ? [String(dn)] : [];
          })()
        ),
        subscriptionType: plan?.subscriptionType?.code || plan?.subscriptionType || 'B2C',
        isOnline: isOnlineValue,
        isPaid: plan?.isPaid === 'Paid' || plan?.isPaid === true ? 'true' : 'false',
        prices: JSON.stringify([
          {
            currencyType: 'INR',
            actualCost: values.price !== '' && values.price != null ? Number(values.price) : 0
          },
          {
            currencyType: 'USD',
            actualCost: values.priceUSD !== '' && values.priceUSD != null ? Number(values.priceUSD) : 0
          },
        ].filter(p => ['INR', 'USD'].includes(p.currencyType))),
        courseIds: JSON.stringify([...new Set((plan?.courses || plan?.courseDetails || []).map(c => Number(c.id)))]),
        assessmentIds: JSON.stringify([...new Set([
          ...(plan?.assessments || plan?.assessmentDetils || []).map(a => Number(a.id)),
          ...(values.selectedAssessmentIds || []).map(Number),
        ])]),
        frequency: values.billingCycle === 'Yearly' ? '1' : '2',
        subscriptionPlan: isAssessment
          ? (values.billingCycle === 'Yearly' ? 'Online Yearly' : 'Online Monthly - Bundle')
          : planType === 'bundle'
            ? (values.billingCycle === 'Yearly' ? 'Online Yearly Bundle' : 'Online Monthly - Bundle')
            : planType === 'single'
              ? (values.billingCycle === 'Yearly' ? 'Online Yearly' : 'Online Monthly')
              : isEbooks
                ? (values.billingCycle === 'Yearly' ? 'Online Yearly Ebook' : 'Online Monthly - Ebook')
                : plan?.subscriptionInfo?.name || '',
        feature_bullets: JSON.stringify((values.featureBullets || []).filter(b => b && b.trim() !== '')),
        is_popular: values.mostPopular ? 'true' : 'false',
        cta_label: values.ctaLabel || '',
        footnote: values.footnote || '',
        plan_category: plan?.planCategory || '',
        categoryId: values.scope === 'category' ? values.category : null,
        tier: values.tier || '',
        scope: values.scope || plan?.scope || 'platform',
        assessment_types: JSON.stringify(values.assessmentTypes || []), max_attempts_per_assessment: values.maxAttempts || 5,
        ...(coverImage ? { coverImage } : {}),
        existingImageUrl: existingImageUrl || '',
        billingtype: values.billingtype
      };
    }
    if (status === 'Archived') {
      await onSave({ ...payload, _action: 'delete' });
      setSaving(false);
      return;
    }
    await onSave(payload);
    setSaving(false);
  };

  const isAssessment = planType === 'assessment';
  const isSingle = planType === 'single';
  const isB2B = planType === 'b2b';
  const isEbooks = planType === 'ebooks';
  const isFree = plan?.isPaid === 'Free';
  const fields = FIELDS_BY_TYPE[planType] || [];
  const hasSections = fields.some((f) => f.section);
  const sectionKeys = [...new Set(fields.map((f) => f.section).filter(Boolean))];

  const planTypeLabel = isSingle ? 'Single Course Plan'
    : planType === 'bundle' ? 'Bundle Plan'
      : isB2B ? 'B2B Plan'
        : isEbooks ? 'E-Books Plan'
          : 'Plan';

  const priceFieldErrors = { price: inrError, priceUSD: usdError };
  const clearPriceError = (key) => {
    if (key === 'price') setInrError('');
    if (key === 'priceUSD') setUsdError('');
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
        onClose={handleCancelClick}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '460px' },
          borderLeft: '1px solid #e5e7eb',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div>
          <h2 className="text-base font-bold text-gray-900">
            Edit {isAssessment ? 'Plan' : planTypeLabel}
          </h2>
          {plan?.name && (
            <p className="text-xs text-gray-400 mt-0.5">{plan.name}</p>
          )}
        </div>
        <IconButton size="small" onClick={handleCancelClick} sx={{ color: '#6b7280' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* ── Body ── */}
      <div className="px-6 py-5 flex-1 overflow-y-auto">

        {/* ── ASSESSMENT plan: full sectioned layout ── */}
        {isAssessment && (
          <>
            <PlanIdentitySection values={values} set={set} onTierChange={handleTierChange} nameError={nameError}
              clearNameError={() => setNameError('')} />
            <SectionDivider />

            <ScopeSection values={values} set={set} examCategories={examCategories} />
            <SectionDivider />

            <PricingSection values={values} set={set} inrError={inrError} usdError={usdError} setInrError={setInrError} setUsdError={setUsdError} />
            <SectionDivider />

            <AccessRulesSection values={values} set={set} error={snackError} />
            <SectionDivider />

            <FeatureBulletsSection values={values} set={set} />
            <SectionDivider />

            <AddAssessmentsSection
              assessmentFetchFn={assessmentFetchFn}
              values={values}
              set={set}
              tier={values.tier || plan?.tier}
              scope={values.scope || plan?.scope}
              selectedCategory={values.category || ''}
              examCategories={examCategories}
              subscriptionTypeCode={(plan?.subscriptionType?.code || 'B2C').toUpperCase()}
              existingAssessmentIds={(plan?.assessments || plan?.assessmentDetils || []).map((a) => Number(a.id))}
            />
          </>
        )}

        {/* ── SINGLE plan ── */}
        {isSingle && (
          <>
            <div className="mb-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2.5">
                Linked Course
              </p>
              <input
                type="text"
                value={plan?.linkedCourse || plan?.courses?.[0]?.title || ''}
                disabled
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-500 bg-gray-50 cursor-not-allowed outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                The linked course cannot be changed after creation.
              </p>
            </div>

            <div className="mb-5 flex items-center gap-2">
              <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${isFree
                ? 'bg-blue-100 text-blue-600 border border-blue-200'
                : 'bg-green-100 text-green-600 border border-green-200'
                }`}>
                {isFree ? 'Free Plan' : 'Paid Plan'}
              </span>
              <p className="text-xs text-gray-400">Plan type cannot be changed here.</p>
            </div>
            {hasSections
              ? sectionKeys.filter(section => isFree ? section !== 'PRICING' : true).map((section) => (
                <div key={section} className="mb-5">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">
                    {section}
                  </p>
                  {fields.filter((f) => f.section === section).map((f) =>
                    renderField(f, values, set, priceFieldErrors, clearPriceError)
                  )}
                </div>
              ))
              : fields.map((f) => renderField(f, values, set, priceFieldErrors, clearPriceError))
            }
            <SectionDivider />
            {!isFree && (<>
              <div className="mb-4">
                <FieldLabel>Billing cycle</FieldLabel>
                <div className="flex border border-gray-200 rounded-[7px] overflow-hidden mt-1 opacity-60 cursor-not-allowed">
                  {['Monthly', 'Yearly'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled
                      className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium select-none cursor-not-allowed"
                      style={{
                        background: values.billingCycle === opt ? '#eff6ff' : '#fff',
                        color: values.billingCycle === opt ? '#1d4ed8' : '#374151',
                        border: values.billingCycle === opt ? '1.5px solid #2563eb' : '1px solid transparent',
                        borderRadius: values.billingCycle === opt ? '6px' : 0,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <FieldLabel required>Billing Type</FieldLabel>
                <div className="flex gap-[10px] opacity-60 cursor-not-allowed">
                  {billingTypes.map(({ value, label }) => (
                    <TierButton
                      key={value}
                      label={label}
                      selected={values.billingtype === value}
                      onClick={() => { }}
                    />
                  ))}
                </div>
              </div>
            </>)}
            <SectionDivider />
            <FeatureBulletsSection values={values} set={set} />
          </>
        )}

        {/* ── B2B plan ── */}
        {isB2B && (
          <B2BEditSection values={values} set={set} />
        )}

        {/* ── E-Books plan ── */}
        {isEbooks && (
          <>
            {fields.map((f) => renderField(f, values, set, priceFieldErrors, clearPriceError))}
            <SectionDivider />
            {!isFree && (
              <div className="mb-4">
                <FieldLabel>Billing Cycle</FieldLabel>
                <div className="flex border border-gray-200 rounded-[7px] overflow-hidden mt-1 opacity-60 cursor-not-allowed">
                  {BILLING_CYCLES.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled
                      className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium select-none cursor-not-allowed"
                      style={{
                        background: values.billingCycle === opt ? '#eff6ff' : '#fff',
                        color: values.billingCycle === opt ? '#1d4ed8' : '#374151',
                        border: values.billingCycle === opt ? '1.5px solid #2563eb' : '1px solid transparent',
                        borderRadius: values.billingCycle === opt ? '6px' : 0,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <SectionDivider />
            <FeatureBulletsSection values={values} set={set} />
            <SectionDivider />
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-3">Cover Image</p>
              <CoverImageUpload
                existingUrl={existingImageUrl}
                coverImage={coverImage}
                preview={coverImgPreview}
                onFileChange={handleCoverFileChange}
                onRemove={handleCoverRemove}
                error={coverImageError}
              />
            </div>
          </>
        )}

        {!isAssessment && !isSingle && !isB2B && !isEbooks && (
          <>
            {fields.map((f) => renderField(f, values, set, priceFieldErrors, clearPriceError))}
            <SectionDivider />
            {!isFree && (<>
              <div className="mb-4">
                <FieldLabel>Billing Cycle</FieldLabel>
                <div className="flex border border-gray-200 rounded-[7px] overflow-hidden mt-1 opacity-60 cursor-not-allowed">
                  {BILLING_CYCLES.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled
                      className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium select-none cursor-not-allowed"
                      style={{
                        background: values.billingCycle === opt ? '#eff6ff' : '#fff',
                        color: values.billingCycle === opt ? '#1d4ed8' : '#374151',
                        border: values.billingCycle === opt ? '1.5px solid #2563eb' : '1px solid transparent',
                        borderRadius: values.billingCycle === opt ? '6px' : 0,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <FieldLabel required>Billing Type</FieldLabel>
                <div className="flex gap-[10px] opacity-60 cursor-not-allowed">
                  {billingTypes.map(({ value, label }) => (
                    <TierButton
                      key={value}
                      label={label}
                      selected={values.billingtype === value}
                      onClick={() => { }}
                    />
                  ))}
                </div>
              </div>
            </>)}

            <SectionDivider />
            <FeatureBulletsSection values={values} set={set} />
            <SectionDivider />
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-3">Cover Image</p>
              <CoverImageUpload
                existingUrl={existingImageUrl}
                coverImage={coverImage}
                preview={coverImgPreview}
                onFileChange={handleCoverFileChange}
                onRemove={handleCoverRemove}
                error={coverImageError}
              />
            </div>
          </>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-2.5 justify-end flex-shrink-0">
        <button type="button" onClick={handleCancelClick} className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
          Cancel
        </button>
        <LoadingButton loading={saving} variant="contained" onClick={handleSave}
          sx={{
            background: '#2563eb', borderRadius: '7px', textTransform: 'none',
            fontSize: '13px', fontWeight: 600, px: '20px',
            '&:hover': { background: '#1d4ed8' },
          }}
        >
          Save Changes
        </LoadingButton>
        {snackError && (
          <p className="text-xs text-red-500 px-6 pb-2 text-right">{snackError}</p>
        )}
      </div>
    </Drawer>
     {showDiscard && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Discard changes?</h2>
            <p className="text-sm text-gray-500 mb-6">
              You have unsaved changes. They will be lost if you close now.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDiscard(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleDiscardConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditDrawer;